import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PermissionService } from '../services/permission.service';
import { AlertService } from '../services/alert.service';
import { MODULE_NAV_CONFIG } from '../models/permission';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// List of public routes that don't require permission checking
const PUBLIC_ROUTES = [
  '/login',
  '/verify-otp',
  '/forgot-password',
  '/reset-password',
  '/profile' // Profile is accessible to all authenticated users
];

export const aclGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const alertService = inject(AlertService);
  const router = inject(Router);

  // Check authentication
  if (!authService.isAuthenticated()) {
    router.navigate(['/login']);
    return false;
  }

  const user = authService.getCurrentUser();
  if (!user) {
    router.navigate(['/login']);
    return false;
  }

  // Public routes or root — always allow
  const isPublicRoute = PUBLIC_ROUTES.some(
    publicRoute => state.url === publicRoute || state.url.startsWith(publicRoute + '/')
  );
  if (isPublicRoute || state.url === '' || state.url === '/') {
    return true;
  }

  // Only enforce ACL for roles 1, 2, 3
  const roleId = user.roleId;
  if (!(roleId == 1 || roleId == 2 || roleId == 3)) {
    return true;
  }

  // Detect if this is an automatic redirect right after login / OTP verification.
  // In that case we silently redirect without showing a toast — the user didn't
  // manually navigate to a restricted route, the app did it automatically.
  const previousUrl = router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString() ?? '';
  const isPostLoginRedirect = PUBLIC_ROUTES.some(
    route => previousUrl === route || previousUrl.startsWith(route + '/')
  );

  // Helper that evaluates access once permissions are loaded
  const evaluateAccess = (): boolean => {
    if (permissionService.canView(state.url)) {
      return true;
    }

    // Only show the toast when the user manually navigated to a restricted route,
    // not when it's an automatic redirect after login / OTP.
    if (!isPostLoginRedirect) {
      alertService.warning('You do not have permission to access this module.', 'Access Denied');
    }

    const firstAllowedRoute = findFirstAllowedRoute(permissionService);
    router.navigate([firstAllowedRoute || '/profile']);
    return false;
  };

  // If permissions are already loaded, evaluate synchronously
  if (permissionService.arePermissionsLoaded()) {
    return evaluateAccess();
  }

  // Permissions not loaded yet — fetch them first, then evaluate
  if (!user.userId) {
    router.navigate(['/login']);
    return false;
  }

  return permissionService.fetchUserPermissions(user.userId).pipe(
    map(() => evaluateAccess()),
    catchError(error => {
      console.error('Error fetching permissions in guard:', error);
      router.navigate(['/login']);
      return of(false);
    })
  );
};

/**
 * Find the first route the user has permission to view
 */
function findFirstAllowedRoute(permissionService: PermissionService): string | null {
  const moduleRoutes = Object.values(MODULE_NAV_CONFIG).map(config => config.route);
  for (const route of moduleRoutes) {
    if (permissionService.canView(route)) {
      return route;
    }
  }
  return null;
}
